import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Loader2, CheckCircle, ExternalLink, Link, FileText, Building2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

type InputMode = 'url' | 'text' | 'manual';

const PROGRESS_STAGES = [
  "Scraping job posting...",
  "Searching contacts...",
  "Researching hiring manager...",
  "Analyzing online activity...",
  "Finalizing report..."
];

const STAGE_DURATION = 20000;

export default function HiringManagerSearch() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Input mode and form fields
  const [inputMode, setInputMode] = useState<InputMode>('url');
  const [jobUrl, setJobUrl] = useState("");
  const [jobPostingText, setJobPostingText] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  
  // Search state
  const [isSearching, setIsSearching] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [reportFound, setReportFound] = useState(false);
  const [foundReportId, setFoundReportId] = useState<string | null>(null);
  
  // Submitted values for polling
  const [submittedData, setSubmittedData] = useState<{
    mode: InputMode;
    jobUrl?: string;
    jobPostingText?: string;
    companyName?: string;
    jobTitle?: string;
  } | null>(null);

  // Progress animation
  useEffect(() => {
    if (!isSearching || reportFound) return;

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 / (PROGRESS_STAGES.length * (STAGE_DURATION / 100)));
        return Math.min(newProgress, 95);
      });
    }, 100);

    const stageInterval = setInterval(() => {
      setCurrentStage(prev => Math.min(prev + 1, PROGRESS_STAGES.length - 1));
    }, STAGE_DURATION);

    const timerInterval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(stageInterval);
      clearInterval(timerInterval);
    };
  }, [isSearching, reportFound]);

  // Poll for results
  const pollForResults = useCallback(async () => {
    if (!submittedData || reportFound) return;

    try {
      let query = supabase
        .from('hiring_manager_reports')
        .select('*')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1);

      // Filter based on what was submitted
      if (submittedData.jobUrl) {
        query = query.eq('job_url', submittedData.jobUrl);
      } else if (submittedData.companyName && submittedData.jobTitle) {
        query = query
          .eq('company_name', submittedData.companyName)
          .eq('job_title', submittedData.jobTitle);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Polling error:', error);
        return;
      }

      if (data && data.length > 0) {
        setReportFound(true);
        setFoundReportId(data[0].id);
        setProgress(100);
        toast({
          title: "Report Ready!",
          description: "Your hiring manager report has been generated.",
        });
      }
    } catch (err) {
      console.error('Polling exception:', err);
    }
  }, [submittedData, reportFound, toast]);

  useEffect(() => {
    if (!isSearching || !submittedData || reportFound) return;

    const pollInterval = setInterval(pollForResults, 5000);
    pollForResults();

    return () => clearInterval(pollInterval);
  }, [isSearching, submittedData, reportFound, pollForResults]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Build request body based on mode
    let body: Record<string, string> = {};
    let submitted: typeof submittedData = { mode: inputMode };

    if (inputMode === 'url') {
      if (!jobUrl.trim()) {
        toast({ title: "Please enter a job URL", variant: "destructive" });
        return;
      }
      body = { job_url: jobUrl.trim() };
      submitted = { ...submitted, jobUrl: jobUrl.trim() };
    } else if (inputMode === 'text') {
      if (!jobPostingText.trim()) {
        toast({ title: "Please paste the job description", variant: "destructive" });
        return;
      }
      body = { job_posting_text: jobPostingText.trim() };
      submitted = { ...submitted, jobPostingText: jobPostingText.trim() };
    } else {
      if (!companyName.trim() || !jobTitle.trim()) {
        toast({ title: "Please enter both company name and job title", variant: "destructive" });
        return;
      }
      body = { company_name: companyName.trim(), job_title: jobTitle.trim() };
      submitted = { ...submitted, companyName: companyName.trim(), jobTitle: jobTitle.trim() };
    }

    setIsSearching(true);
    setCurrentStage(0);
    setProgress(0);
    setElapsedTime(0);
    setSubmittedData(submitted);
    setReportFound(false);
    setFoundReportId(null);

    try {
      const response = await fetch(
        'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/trigger-report',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to trigger report');
      }

      toast({
        title: "Search Started",
        description: "Looking for the hiring manager. This typically takes 2-3 minutes.",
      });
    } catch (err: any) {
      console.error('Submit error:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to start the search. Please try again.",
        variant: "destructive",
      });
      setIsSearching(false);
      setSubmittedData(null);
    }
  };

  const handleViewReport = () => {
    if (foundReportId) {
      navigate('/hiring-manager-reports');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isSearching) {
    return (
      <Card className="border-2 border-blue-400 bg-white shadow-lg">
        <CardHeader className="text-center bg-blue-50 border-b border-blue-200">
          <CardTitle className="flex items-center justify-center gap-2">
            {reportFound ? (
              <>
                <CheckCircle className="w-6 h-6 text-success" />
                Report Complete!
              </>
            ) : (
              <>
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                Searching for Hiring Manager...
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{reportFound ? "Complete" : PROGRESS_STAGES[currentStage]}</span>
              <span>{formatTime(elapsedTime)}</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          <div className="space-y-2">
            {PROGRESS_STAGES.map((stage, index) => (
              <div
                key={stage}
                className={`flex items-center gap-2 text-sm transition-all duration-300 ${
                  index < currentStage || reportFound
                    ? 'text-success'
                    : index === currentStage && !reportFound
                    ? 'text-blue-600 font-medium'
                    : 'text-muted-foreground'
                }`}
              >
                {index < currentStage || reportFound ? (
                  <CheckCircle className="w-4 h-4" />
                ) : index === currentStage && !reportFound ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <div className="w-4 h-4 rounded-full border border-muted-foreground/30" />
                )}
                {stage}
              </div>
            ))}
          </div>

          <div className="bg-blue-50 rounded-lg p-4 text-sm text-muted-foreground border border-blue-200">
            {submittedData?.mode === 'url' && (
              <p className="truncate"><strong>URL:</strong> {submittedData.jobUrl}</p>
            )}
            {submittedData?.mode === 'text' && (
              <p className="line-clamp-3"><strong>Job Description:</strong> {submittedData.jobPostingText?.substring(0, 200)}...</p>
            )}
            {submittedData?.mode === 'manual' && (
              <>
                <p><strong>Company:</strong> {submittedData.companyName}</p>
                <p><strong>Job Title:</strong> {submittedData.jobTitle}</p>
              </>
            )}
          </div>

          {reportFound && (
            <Button onClick={handleViewReport} className="w-full gap-2 bg-blue-600 hover:bg-blue-700">
              <ExternalLink className="w-4 h-4" />
              View Full Report
            </Button>
          )}

          {!reportFound && (
            <div className="text-center space-y-1">
              <p className="text-sm text-muted-foreground">
                This takes 5-8 minutes. You can leave this page—we'll notify you in your TailorWiz account when your Report is ready.
              </p>
              <Button variant="link" size="sm" onClick={() => navigate('/hiring-manager-reports')} className="text-xs text-blue-600">
                View My Reports →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-blue-400 bg-white shadow-lg">
      <CardHeader className="bg-blue-50 border-b border-blue-200">
        <CardTitle className="flex items-center gap-2">
          <Search className="w-5 h-5 text-blue-600" />
          Find the Hiring Manager
        </CardTitle>
        <CardDescription>
          Enter job details to identify the decision-maker
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs value={inputMode} onValueChange={(v) => setInputMode(v as InputMode)} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="url" className="gap-1.5 text-xs sm:text-sm">
                <Link className="w-4 h-4" />
                <span className="hidden sm:inline">Job URL</span>
                <span className="sm:hidden">URL</span>
              </TabsTrigger>
              <TabsTrigger value="text" className="gap-1.5 text-xs sm:text-sm">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Paste Text</span>
                <span className="sm:hidden">Text</span>
              </TabsTrigger>
              <TabsTrigger value="manual" className="gap-1.5 text-xs sm:text-sm">
                <Building2 className="w-4 h-4" />
                <span className="hidden sm:inline">Manual</span>
                <span className="sm:hidden">Manual</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {inputMode === 'url' && (
            <div className="space-y-2">
              <Label htmlFor="jobUrl">Job Posting URL</Label>
              <Input
                id="jobUrl"
                type="url"
                placeholder="https://linkedin.com/jobs/view/..."
                value={jobUrl}
                onChange={(e) => setJobUrl(e.target.value)}
                className="border-2 border-gray-400"
              />
              <p className="text-xs text-muted-foreground">
                Paste the direct URL of the specific job posting—not a Google search results page or a page listing multiple jobs. Click into the actual job you want to apply for, then copy that URL.
              </p>
            </div>
          )}

          {inputMode === 'text' && (
            <div className="space-y-2">
              <Label htmlFor="jobPostingText">Job Description</Label>
              <Textarea
                id="jobPostingText"
                placeholder="Paste the full job posting here..."
                value={jobPostingText}
                onChange={(e) => setJobPostingText(e.target.value)}
                className="min-h-[200px] border-2 border-gray-400"
              />
              <p className="text-xs text-muted-foreground">
                Copy and paste the entire job posting. We'll extract the company and role.
              </p>
            </div>
          )}

          {inputMode === 'manual' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder="e.g., Acme Corporation"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="border-2 border-gray-400"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jobTitle">Job Title</Label>
                <Input
                  id="jobTitle"
                  type="text"
                  placeholder="e.g., Senior Software Engineer"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  className="border-2 border-gray-400"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the company and job title manually. We'll search for the hiring manager.
              </p>
            </div>
          )}

          <Button type="submit" className="w-full gap-2 bg-blue-600 hover:bg-blue-700">
            <Search className="w-4 h-4" />
            Find Hiring Manager
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
